/**
 * Renders a BreadcrumbList JSON-LD structured data script tag.
 *
 * Usage inside an Inertia <Head> block:
 *   <BreadcrumbSchema
 *     canonical={meta.canonical}
 *     items={[
 *       { name: "Guides", path: "/guides" },
 *       { name: "Estate Agent Fees" },
 *     ]}
 *   />
 *
 * The "Home" crumb is always prepended automatically.
 * The last item should omit `path` (current page).
 */

interface BreadcrumbItem {
  name: string
  path?: string
}

export function BreadcrumbSchema({
  canonical,
  items,
}: {
  canonical: string
  items: BreadcrumbItem[]
}) {
  const origin = new URL(canonical).origin

  const listItems = [
    { "@type": "ListItem" as const, position: 1, name: "Home", item: `${origin}/` },
    ...items.map((crumb, i) => ({
      "@type": "ListItem" as const,
      position: i + 2,
      name: crumb.name,
      ...(crumb.path ? { item: `${origin}${crumb.path}` } : {}),
    })),
  ]

  return (
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: listItems,
      })}
    </script>
  )
}
